/**
 * Manual memory entry creation. First real usage of react-hook-form +
 * zodResolver + components/ui/form.tsx in this repo (deliberate — see
 * userMemory.types.ts for the schema).
 */

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useCreateMemoryEntry, useMemoryErrorToast, useMemoryTemplateOptions } from '../useUserMemoryQueries';
import {
  type CreateMemoryEntryFormValues,
  MEMORY_CATEGORIES,
  MEMORY_CATEGORY_LABELS,
  MEMORY_INSTRUCTION_MAX_LENGTH,
  createMemoryEntrySchema,
} from '../userMemory.types';

const NO_TEMPLATE_VALUE = 'none';

export const CreateMemoryForm = () => {
  const { toast } = useToast();
  const onError = useMemoryErrorToast();
  const createMutation = useCreateMemoryEntry();
  const { data: templates = [] } = useMemoryTemplateOptions();

  const form = useForm<CreateMemoryEntryFormValues>({
    resolver: zodResolver(createMemoryEntrySchema),
    defaultValues: {
      category: 'style',
      instructionText: '',
      templateId: null,
    },
  });

  const instructionText = form.watch('instructionText') ?? '';

  const onSubmit = (values: CreateMemoryEntryFormValues) => {
    createMutation.mutate(
      {
        category: values.category,
        instruction_text: values.instructionText,
        template_id: values.templateId,
      },
      {
        onSuccess: () => {
          toast({ title: 'Minni búið til', description: 'Reglan er nú virk og verður notuð í næstu samantektum.' });
          form.reset({ category: 'style', instructionText: '', templateId: null });
        },
        onError,
      }
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Flokkur</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Veldu flokk" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {MEMORY_CATEGORIES.map((category) => (
                      <SelectItem key={category} value={category}>
                        {MEMORY_CATEGORY_LABELS[category]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="templateId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Sniðmát (valkvætt)</FormLabel>
                <Select
                  value={field.value === null ? NO_TEMPLATE_VALUE : String(field.value)}
                  onValueChange={(value) => field.onChange(value === NO_TEMPLATE_VALUE ? null : Number(value))}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Almennt (öll sniðmát)" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={NO_TEMPLATE_VALUE}>Almennt (öll sniðmát)</SelectItem>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={String(template.id)}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="instructionText"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Leiðbeiningar</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="t.d. Notaðu stuttar, tölusettar línur í stað málsgreina í skoðunarhluta."
                  rows={4}
                  maxLength={MEMORY_INSTRUCTION_MAX_LENGTH}
                  className="resize-none"
                />
              </FormControl>
              <div className="flex justify-between text-muted-foreground text-xs">
                <span>Lýstu stíl, uppbyggingu, orðalagi eða fagorðum — ekki klínísku innihaldi.</span>
                <span>
                  {instructionText.length}/{MEMORY_INSTRUCTION_MAX_LENGTH}
                </span>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={createMutation.isPending}>
          {createMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Bæta við minni
        </Button>
      </form>
    </Form>
  );
};
